import React, { useRef, useEffect, useState } from "react";
import { select } from "d3-selection";
import { geoPath, geoNaturalEarth1 } from "d3-geo";
import { transition } from "d3-transition";
import { json } from "d3-fetch";
import simplify from "@turf/simplify";

const highlighted = new Set([
  "BRA", // Brazil
  "PER", // Peru
  "BOL", // Bolivia
  "UKR", // Ukraine
  "MAR", // Morocco
  "SEN", // Senegal
  "GIN", // Guinea
  "CIV", // Ivory Coast
  "TGO", // Togo
  "NGA", // Nigeria
  "CAF", // Central African Republic
  "COD", // Democratic Republic of the Congo
  "AGO", // Angola
  "NAM", // Namibia
  "ZAF", // South Africa
  "MWI", // Malawi
  "KEN", // Kenya
  "SOM", // Somalia
  "ETH", // Ethiopia
  "KAZ", // Kazakhstan
  "UZB", // Uzbekistan
  "TUR", // Turkey
  "IRN", // Iran
  "PAK", // Pakistan
  "PHL", // Philippines
  "IND", // India
  "IDN", // Indonesia
  "NPL", // Nepal
  "MMR", // Myanmar
  "THA", // Thailand
  "VNM", // Vietnam
  "MYS", // Malaysia
  "GBR", // United Kingdom
  "FRA", // France
  "ESP", // Spain
  "DNK", // Denmark
  "DEU", // Germany
  "CHE", // Switzerland
  "ARE", // United Arab Emirates
  "USA", // United States
  "CAN", // Canada
]);

const InteractiveMap = ({ currentCountry }) => {
  const svgRef = useRef();
  const [topo, setTopo] = useState(null);

  // 🪶 Combine simplification + coordinate rounding + property pruning
  const optimizeGeoJSON = (geojson, tolerance = 0.002) => {
    const simplified = {
      type: "FeatureCollection",
      features: geojson.features.map((f) => {
        const s = simplify(f, { tolerance, highQuality: false });
        const props = { ADM0_A3: f.properties.ADM0_A3 };
        // Truncate coordinates in-place (2–3 decimals is enough)
        const cleanCoords = (coords) =>
          Array.isArray(coords[0])
            ? coords.map(cleanCoords)
            : [+coords[0].toFixed(3), +coords[1].toFixed(3)];
        return {
          ...s,
          geometry: {
            ...s.geometry,
            coordinates: cleanCoords(s.geometry.coordinates),
          },
          properties: props,
        };
      }),
    };
    return simplified;
  };

  // 📦 Load and simplify once
  useEffect(() => {
    json(
      "https://raw.githubusercontent.com/nvkelso/natural-earth-vector/v5.0.1/geojson/ne_50m_admin_0_countries.geojson"
    ).then((data) => {
      // ⚙️ Higher tolerance = fewer points = smoother performance
      setTopo(optimizeGeoJSON(data, 0.001));
    });
  }, []);

  // 🌍 Draw initial map
  useEffect(() => {
    if (!topo) return;
    const svg = select(svgRef.current).attr("viewBox", "0 0 800 600");
    const g = svg.append("g");

    const projection = geoNaturalEarth1()
      .scale(230)
      .center([0, 20])
      .translate([400, 300]);

    const path = geoPath().projection(projection);

    // 🖌️ Single pass for fill assignment
    g.selectAll("path")
      .data(topo.features)
      .enter()
      .append("path")
      .attr("d", path)
      .attr("fill", (d) =>
        highlighted.has(d.properties.ADM0_A3) ? "#b491d0" : "#d3d3d3"
      )
      .attr("stroke", "transparent")
      .attr("class", "Country")
      .style("opacity", 0.85)
      .on("mouseover", function () {
        select(this).attr("stroke", "white").style("opacity", 1);
      })
      .on("mouseleave", function () {
        select(this).attr("stroke", "transparent").style("opacity", 0.85);
      });

    return () => svg.selectAll("*").remove();
  }, [topo]);

  // 🔍 Smooth zoom + color transitions
  useEffect(() => {
    if (!topo) return;
    const svg = select(svgRef.current);
    const g = svg.select("g");
    const projection = geoNaturalEarth1()
      .scale(230)
      .center([0, 20])
      .translate([400, 300]);
    const path = geoPath().projection(projection);
    const t = transition().duration(700);

    g.selectAll(".Country")
      .transition(t)
      .attr("fill", (d) =>
        highlighted.has(d.properties.ADM0_A3) ? "#b491d0" : "#d3d3d3"
      )
      .attr("stroke", "transparent");

    if (!currentCountry) {
      g.transition(t).attr("transform", "");
      return;
    }

    const feature = topo.features.find(
      (f) => f.properties.ADM0_A3 === currentCountry
    );
    if (!feature) return;

    // Highlight selected
    g.selectAll(".Country")
      .transition(t)
      .attr("fill", (d) =>
        d.properties.ADM0_A3 === currentCountry ? "#b491d0" : "#d3d3d3"
      )
      .attr("stroke", "white")
      .attr("stroke-width", (d) =>
        d.properties.ADM0_A3 === currentCountry ? 0.5 : 0.2
      );

    const [x0, y0, x1, y1] = path.bounds(feature).flat();
    const dx = x1 - x0;
    const dy = y1 - y0;
    const scale = Math.min(8, 0.9 / Math.max(dx / 800, dy / 600));
    const x = (x0 + x1) / 2,
      y = (y0 + y1) / 2;
    const translate = [400 - scale * x + 150, 300 - scale * y];

    g.transition(t).attr(
      "transform",
      `translate(${translate}) scale(${scale})`
    );
  }, [currentCountry, topo]);

  return (
    <svg
      className="worldMap"
      ref={svgRef}
      width="100%"
      height="auto"
      preserveAspectRatio="xMidYMid meet"
    />
  );
};

export default InteractiveMap;